import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X, RotateCcw, Play, Pause,
  ChevronUp, ChevronDown, ChevronLeft, ChevronRight, RotateCw, ArrowDownToLine,
} from "lucide-react";

/* ═══════════════════════════════════════════════════════════
   Games.tsx — drop-in, reusable mini-game kit
   (Chess, Snake, Blocks) with a shared launcher/shell.

   USAGE — quickest path, one component does everything:

     import GamesPanel from "./Games";
     <GamesPanel />

   USAGE — custom layout, pick your own pieces:

     import { GAMES, GameTile, GameShell, useGamesState } from "./Games";
     const { activeGame, isMobile, open, close } = useGamesState();
     {GAMES.map(g => <GameTile key={g.id} game={g} onPlay={() => open(g.id)} />)}
     <GameShell game={activeGame} onClose={close} isMobile={isMobile} />

   THEMING — pass a `theme` prop (partial) anywhere down the tree,
   or wrap with <GamesThemeProvider theme={{...}}> once.
═══════════════════════════════════════════════════════════ */

/* ───────────────────────── THEME ───────────────────────── */

export interface GamesTheme {
  bg: string;
  panelBg: string;
  dark: string;
  textMuted: string;
  red: string;
  redDark: string;
  gold: string;
  goldDark: string;
  goldSoft: string;
  border: string;
  green: string;
}

export const defaultGamesTheme: GamesTheme = {
  bg: "#FBF6EE",
  panelBg: "#FFFFFF",
  dark: "#1B0E06",
  textMuted: "#7A6653",
  red: "#C8202E",
  redDark: "#9E1521",
  gold: "#F2A93E",
  goldDark: "#D4900F",
  goldSoft: "#FCEFD4",
  border: "#EEDFC4",
  green: "#3F8A4C",
};

const GamesThemeCtx = React.createContext<GamesTheme>(defaultGamesTheme);

export const GamesThemeProvider: React.FC<{ theme?: Partial<GamesTheme>; children: React.ReactNode }> = ({ theme, children }) => {
  const merged = useMemo(() => ({ ...defaultGamesTheme, ...theme }), [theme]);
  return <GamesThemeCtx.Provider value={merged}>{children}</GamesThemeCtx.Provider>;
};

const useGamesTheme = (override?: Partial<GamesTheme>): GamesTheme => {
  const ctx = React.useContext(GamesThemeCtx);
  return useMemo(() => (override ? { ...ctx, ...override } : ctx), [ctx, override]);
};

/* ─────────────────────── GAME LIST ─────────────────────── */

export type GameId = "chess" | "block" | "snake";

export interface GameDef {
  id: GameId;
  label: string;
  face: string; // front face color (falls back to theme if omitted per-instance)
  side: string; // 3D drop-shadow side color
}

export const GAMES: GameDef[] = [
  { id: "chess", label: "Chess", face: "#1B0E06", side: "#3A2418" },
  { id: "block", label: "Blocks", face: "#C8202E", side: "#8E1520" },
  { id: "snake", label: "Snake", face: "#3F8A4C", side: "#2C6135" },
];

const GAME_TITLES: Record<GameId, string> = { chess: "Chess", block: "Blocks", snake: "Snake" };

const GAME_HINTS: Record<GameId, string> = {
  chess: "Tap a piece, then a highlighted square. White moves first — pass the device after each turn.",
  block: "Arrows move, Up rotates, Space drops. Fill a full row to clear it.",
  snake: "Arrow keys or WASD to steer. Eat the red dot, don't bite yourself.",
};

/* ───────────────────────── STYLES ──────────────────────── */

const buildGamesCSS = (t: GamesTheme) => `
  .gk-scope button { font-family: inherit; }

  @keyframes gkShimmer { 0%{background-position:0% 50%;} 100%{background-position:200% 50%;} }

  .gk-game { position: relative; background: none; border: none; cursor: pointer; padding: 0; transition: transform .18s ease; }
  .gk-game:hover { transform: translateY(-6px); }
  .gk-game-face {
    position: relative; width: 132px; height: 132px; border-radius: 24px;
    display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 10px;
    border: 3px solid ${t.dark};
  }
  .gk-game-label { font-family: 'Sora', sans-serif; font-size: 13px; font-weight: 800; color: #fff; letter-spacing: .06em; text-transform: uppercase; }

  .gk-shell-head {
    display: flex; align-items: center; justify-content: space-between; gap: 12px;
    padding: 16px 20px; border-bottom: 1px solid ${t.border};
  }
  .gk-shell-title { font-family: 'Sora', sans-serif; font-size: 17px; font-weight: 900; color: ${t.dark}; }
  .gk-hint {
    font-style: italic; font-size: 12.5px; font-weight: 500; color: ${t.textMuted};
    padding: 14px 24px 0; text-align: center; line-height: 1.6;
  }
  .gk-iconbtn {
    width: 34px; height: 34px; border-radius: 12px; border: 1px solid ${t.border}; background: #fff;
    display: flex; align-items: center; justify-content: center; cursor: pointer;
    transition: background .15s, border-color .15s; flex-shrink: 0;
  }
  .gk-iconbtn:hover { background: ${t.goldSoft}; border-color: ${t.gold}; }

  .gk-pad {
    width: 46px; height: 46px; border-radius: 14px; border: 2px solid ${t.dark}; background: #fff;
    display: flex; align-items: center; justify-content: center; cursor: pointer;
    transition: background .12s, transform .08s;
  }
  .gk-pad:active { background: ${t.goldSoft}; transform: translateY(2px); }

  .gk-restart {
    display: inline-flex; align-items: center; gap: 7px;
    background: ${t.red}; color: #fff; border: 2px solid ${t.dark}; border-radius: 12px;
    padding: 10px 20px; font-family: 'Sora', sans-serif; font-size: 13px; font-weight: 800; cursor: pointer;
    transition: background .18s;
  }
  .gk-restart:hover { background: ${t.redDark}; }

  .gk-chip { font-size: 11px; font-weight: 800; letter-spacing: .1em; text-transform: uppercase; color: ${t.textMuted}; }
  .gk-score { font-family: 'Sora', sans-serif; font-size: 18px; font-weight: 900; color: ${t.dark}; }

  @media (max-width: 768px) {
    .gk-game-face { width: 108px !important; height: 108px !important; }
  }

  @media (prefers-reduced-motion: reduce) { .gk-scope * { animation: none !important; } }
`;

/** Injects the CSS classes the kit needs. Render once anywhere in the tree
 *  (GameShell and GameTile already do this automatically, so you usually
 *  don't need to use this directly — it's here for custom layouts). */
export const GamesStyles: React.FC<{ theme?: Partial<GamesTheme> }> = ({ theme }) => {
  const t = useGamesTheme(theme);
  return <style>{buildGamesCSS(t)}</style>;
};

/* ═══════════════════════════════════════════════════════════
   SNAKE
═══════════════════════════════════════════════════════════ */
const S_GRID = 15;
const S_TICK = 140;

type Pt = { x: number; y: number };

const randFood = (occupied: Pt[]): Pt => {
  let p: Pt;
  do {
    p = { x: Math.floor(Math.random() * S_GRID), y: Math.floor(Math.random() * S_GRID) };
  } while (occupied.some(o => o.x === p.x && o.y === p.y));
  return p;
};

export const SnakeGame: React.FC<{ theme?: Partial<GamesTheme> }> = ({ theme }) => {
  const HEART = useGamesTheme(theme);
  const [snake, setSnake] = useState<Pt[]>([{ x: 7, y: 7 }, { x: 6, y: 7 }, { x: 5, y: 7 }]);
  const [food, setFood] = useState<Pt>({ x: 11, y: 7 });
  const [score, setScore] = useState(0);
  const [dead, setDead] = useState(false);
  const [paused, setPaused] = useState(false);

  const dirRef = useRef<Pt>({ x: 1, y: 0 });
  const appliedRef = useRef<Pt>({ x: 1, y: 0 });

  const turn = useCallback((x: number, y: number) => {
    const cur = appliedRef.current;
    if (cur.x === -x && cur.y === -y) return;
    dirRef.current = { x, y };
  }, []);

  const restart = useCallback(() => {
    const s = [{ x: 7, y: 7 }, { x: 6, y: 7 }, { x: 5, y: 7 }];
    setSnake(s); setFood(randFood(s)); setScore(0); setDead(false); setPaused(false);
    dirRef.current = { x: 1, y: 0 }; appliedRef.current = { x: 1, y: 0 };
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const k = e.key.toLowerCase();
      if (["arrowup", "arrowdown", "arrowleft", "arrowright", " "].includes(k)) e.preventDefault();
      if (k === "arrowup" || k === "w") turn(0, -1);
      else if (k === "arrowdown" || k === "s") turn(0, 1);
      else if (k === "arrowleft" || k === "a") turn(-1, 0);
      else if (k === "arrowright" || k === "d") turn(1, 0);
      else if (k === " ") setPaused(p => !p);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [turn]);

  useEffect(() => {
    const onVis = () => { if (document.hidden) setPaused(true); };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  useEffect(() => {
    if (dead || paused) return;
    const t = setInterval(() => {
      setSnake(prev => {
        const d = dirRef.current;
        appliedRef.current = d;
        const head = { x: prev[0].x + d.x, y: prev[0].y + d.y };
        const hitWall = head.x < 0 || head.y < 0 || head.x >= S_GRID || head.y >= S_GRID;
        const hitSelf = prev.slice(0, -1).some(s => s.x === head.x && s.y === head.y);
        if (hitWall || hitSelf) { setDead(true); return prev; }

        const next = [head, ...prev];
        if (head.x === food.x && head.y === food.y) {
          setScore(s => s + 1);
          setFood(randFood(next));
        } else {
          next.pop();
        }
        return next;
      });
    }, S_TICK);
    return () => clearInterval(t);
  }, [dead, paused, food]);

  const cell = 20;

  return (
    <div className="gk-scope" style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 14 }}>
      <GamesStyles theme={theme} />
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: S_GRID * cell }}>
        <div>
          <div className="gk-chip">Score</div>
          <div className="gk-score">{score}</div>
        </div>
        <button className="gk-iconbtn" onClick={() => setPaused(p => !p)} disabled={dead} title={paused ? "Resume" : "Pause"}>
          {paused ? <Play size={15} color={HEART.dark} /> : <Pause size={15} color={HEART.dark} />}
        </button>
      </div>

      <div style={{
        position: "relative", width: S_GRID * cell, height: S_GRID * cell,
        background: HEART.goldSoft, border: `3px solid ${HEART.dark}`, borderRadius: 14, overflow: "hidden",
      }}>
        {Array.from({ length: S_GRID * S_GRID }).map((_, i) => {
          const x = i % S_GRID, y = Math.floor(i / S_GRID);
          return (x + y) % 2 === 0 ? (
            <div key={i} style={{ position: "absolute", left: x * cell, top: y * cell, width: cell, height: cell, background: "rgba(27,14,6,0.035)" }} />
          ) : null;
        })}

        <div style={{
          position: "absolute", left: food.x * cell + 3, top: food.y * cell + 3,
          width: cell - 6, height: cell - 6, borderRadius: "50%",
          background: `radial-gradient(circle at 34% 30%, ${HEART.red} 0%, ${HEART.redDark} 100%)`,
          boxShadow: "0 2px 6px rgba(200,32,46,0.5)",
        }} />

       {snake.map((s, i) => (
  <div key={`${s.x}-${s.y}-${i}`} style={{
    position: "absolute", left: s.x * cell + 1.5, top: s.y * cell + 1.5,
    width: cell - 3, height: cell - 3, borderRadius: i === 0 ? 7 : 5,
    background: i === 0
      ? `radial-gradient(circle at 34% 30%, #56A863 0%, ${HEART.green} 60%, #2C6135 100%)`
      : HEART.green,
    opacity: i === 0 ? 1 : Math.max(0.5, 1 - i * 0.035),
  }} />
))}

        <AnimatePresence>
          {dead && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              style={{ position: "absolute", inset: 0, background: "rgba(27,14,6,0.78)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 14 }}>
              <span style={{ fontFamily: "'Sora',sans-serif", fontSize: 22, fontWeight: 900, color: "#fff" }}>Game Over</span>
              <span style={{ fontSize: 13, color: "rgba(255,255,255,0.75)", fontWeight: 600 }}>You scored {score}</span>
              <button className="gk-restart" onClick={restart}><RotateCcw size={13} /> Play again</button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 46px)", gridTemplateRows: "repeat(2, 46px)", gap: 8, justifyContent: "center" }}>
        <div />
        <button className="gk-pad" onClick={() => turn(0, -1)} aria-label="Up"><ChevronUp size={20} color={HEART.dark} /></button>
        <div />
        <button className="gk-pad" onClick={() => turn(-1, 0)} aria-label="Left"><ChevronLeft size={20} color={HEART.dark} /></button>
        <button className="gk-pad" onClick={() => turn(0, 1)} aria-label="Down"><ChevronDown size={20} color={HEART.dark} /></button>
        <button className="gk-pad" onClick={() => turn(1, 0)} aria-label="Right"><ChevronRight size={20} color={HEART.dark} /></button>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   BLOCKS (tetromino stacker)
═══════════════════════════════════════════════════════════ */
const B_COLS = 10;
const B_ROWS = 16;

const SHAPES: { cells: number[][]; color: string }[] = [
  { cells: [[1, 1, 1, 1]], color: "#2563EB" },
  { cells: [[1, 1], [1, 1]], color: "#F2A93E" },
  { cells: [[0, 1, 0], [1, 1, 1]], color: "#9333EA" },
  { cells: [[0, 1, 1], [1, 1, 0]], color: "#3F8A4C" },
  { cells: [[1, 1, 0], [0, 1, 1]], color: "#C8202E" },
  { cells: [[1, 0, 0], [1, 1, 1]], color: "#D4900F" },
  { cells: [[0, 0, 1], [1, 1, 1]], color: "#0D9488" },
];

type Cell = string | null;
type Piece = { cells: number[][]; color: string; x: number; y: number };

const emptyBoard = (): Cell[][] => Array.from({ length: B_ROWS }, () => Array<Cell>(B_COLS).fill(null));

const spawnPiece = (): Piece => {
  const s = SHAPES[Math.floor(Math.random() * SHAPES.length)];
  return { cells: s.cells, color: s.color, x: Math.floor((B_COLS - s.cells[0].length) / 2), y: 0 };
};

const rotateCells = (cells: number[][]): number[][] => {
  const rows = cells.length, cols = cells[0].length;
  const out: number[][] = Array.from({ length: cols }, () => Array(rows).fill(0));
  for (let r = 0; r < rows; r++) for (let c = 0; c < cols; c++) out[c][rows - 1 - r] = cells[r][c];
  return out;
};

const collides = (board: Cell[][], p: Piece, dx = 0, dy = 0, cells = p.cells): boolean => {
  for (let r = 0; r < cells.length; r++) {
    for (let c = 0; c < cells[r].length; c++) {
      if (!cells[r][c]) continue;
      const nx = p.x + c + dx, ny = p.y + r + dy;
      if (nx < 0 || nx >= B_COLS || ny >= B_ROWS) return true;
      if (ny >= 0 && board[ny][nx]) return true;
    }
  }
  return false;
};

export const BlocksGame: React.FC<{ theme?: Partial<GamesTheme> }> = ({ theme }) => {
  const HEART = useGamesTheme(theme);
  const [board, setBoard] = useState<Cell[][]>(emptyBoard);
  const [piece, setPiece] = useState<Piece>(spawnPiece);
  const [score, setScore] = useState(0);
  const [over, setOver] = useState(false);
  const [paused, setPaused] = useState(false);

  const boardRef = useRef(board); boardRef.current = board;
  const pieceRef = useRef(piece); pieceRef.current = piece;

  const lockAndNext = useCallback((p: Piece) => {
    const b = boardRef.current.map(row => [...row]);
    p.cells.forEach((row, r) => row.forEach((v, c) => {
      if (v && p.y + r >= 0) b[p.y + r][p.x + c] = p.color;
    }));

    let cleared = 0;
    const kept = b.filter(row => {
      const full = row.every(cell => cell !== null);
      if (full) cleared++;
      return !full;
    });
    while (kept.length < B_ROWS) kept.unshift(Array<Cell>(B_COLS).fill(null));
    if (cleared) setScore(s => s + [0, 40, 100, 300, 1200][cleared]);

    const next = spawnPiece();
    if (collides(kept, next)) { setBoard(kept); setOver(true); return; }
    setBoard(kept);
    setPiece(next);
  }, []);

  const step = useCallback(() => {
    const p = pieceRef.current;
    if (collides(boardRef.current, p, 0, 1)) lockAndNext(p);
    else setPiece({ ...p, y: p.y + 1 });
  }, [lockAndNext]);

  const move = useCallback((dx: number) => {
    const p = pieceRef.current;
    if (!collides(boardRef.current, p, dx, 0)) setPiece({ ...p, x: p.x + dx });
  }, []);

  const rotate = useCallback(() => {
    const p = pieceRef.current;
    const rc = rotateCells(p.cells);
    for (const kick of [0, -1, 1, -2, 2]) {
      if (!collides(boardRef.current, p, kick, 0, rc)) { setPiece({ ...p, cells: rc, x: p.x + kick }); return; }
    }
  }, []);

  const hardDrop = useCallback(() => {
    const p = pieceRef.current;
    let dy = 0;
    while (!collides(boardRef.current, p, 0, dy + 1)) dy++;
    lockAndNext({ ...p, y: p.y + dy });
  }, [lockAndNext]);

  const restart = useCallback(() => {
    setBoard(emptyBoard()); setPiece(spawnPiece()); setScore(0); setOver(false); setPaused(false);
  }, []);

  useEffect(() => {
    const onVis = () => { if (document.hidden) setPaused(true); };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  useEffect(() => {
    if (over || paused) return;
    const t = setInterval(step, 600);
    return () => clearInterval(t);
  }, [over, paused, step]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (over) return;
      const k = e.key.toLowerCase();
      if (["arrowup", "arrowdown", "arrowleft", "arrowright", " "].includes(k)) e.preventDefault();
      if (k === "arrowleft" || k === "a") move(-1);
      else if (k === "arrowright" || k === "d") move(1);
      else if (k === "arrowdown" || k === "s") step();
      else if (k === "arrowup" || k === "w") rotate();
      else if (k === " ") hardDrop();
      else if (k === "p") setPaused(p => !p);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [move, step, rotate, hardDrop, over]);

  const cell = 20;

  const view = useMemo(() => {
    const b = board.map(row => [...row]);
    piece.cells.forEach((row, r) => row.forEach((v, c) => {
      const y = piece.y + r, x = piece.x + c;
      if (v && y >= 0 && y < B_ROWS && x >= 0 && x < B_COLS) b[y][x] = piece.color;
    }));
    return b;
  }, [board, piece]);

  return (
    <div className="gk-scope" style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 14 }}>
      <GamesStyles theme={theme} />
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: B_COLS * cell }}>
        <div>
          <div className="gk-chip">Score</div>
          <div className="gk-score">{score}</div>
        </div>
        <button className="gk-iconbtn" onClick={() => setPaused(p => !p)} disabled={over} title={paused ? "Resume" : "Pause"}>
          {paused ? <Play size={15} color={HEART.dark} /> : <Pause size={15} color={HEART.dark} />}
        </button>
      </div>

      <div style={{
        position: "relative", width: B_COLS * cell, height: B_ROWS * cell,
        background: HEART.goldSoft, border: `3px solid ${HEART.dark}`, borderRadius: 14, overflow: "hidden",
      }}>
        {view.map((row, y) => row.map((c, x) => (
          <div key={`${x}-${y}`} style={{
            position: "absolute", left: x * cell, top: y * cell, width: cell, height: cell,
            background: c ?? ((x + y) % 2 === 0 ? "rgba(27,14,6,0.035)" : "transparent"),
            border: c ? "1px solid rgba(255,255,255,0.28)" : "none",
            borderRadius: c ? 4 : 0,
            boxShadow: c ? "inset 0 -3px 5px rgba(0,0,0,0.22)" : "none",
          }} />
        )))}

        <AnimatePresence>
          {over && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              style={{ position: "absolute", inset: 0, background: "rgba(27,14,6,0.78)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 14, padding: 16, textAlign: "center" }}>
              <span style={{ fontFamily: "'Sora',sans-serif", fontSize: 21, fontWeight: 900, color: "#fff" }}>Stack Full</span>
              <span style={{ fontSize: 13, color: "rgba(255,255,255,0.75)", fontWeight: 600 }}>You scored {score}</span>
              <button className="gk-restart" onClick={restart}><RotateCcw size={13} /> Play again</button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <div style={{ display: "flex", gap: 8 }}>
        <button className="gk-pad" onClick={() => move(-1)} aria-label="Left"><ChevronLeft size={20} color={HEART.dark} /></button>
        <button className="gk-pad" onClick={rotate} aria-label="Rotate"><RotateCw size={18} color={HEART.dark} /></button>
        <button className="gk-pad" onClick={step} aria-label="Down"><ChevronDown size={20} color={HEART.dark} /></button>
        <button className="gk-pad" onClick={() => move(1)} aria-label="Right"><ChevronRight size={20} color={HEART.dark} /></button>
        <button className="gk-pad" onClick={hardDrop} aria-label="Drop"><ArrowDownToLine size={18} color={HEART.red} /></button>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   CHESS (pass-and-play)
   Standard piece movement. No castling / en passant; pawns
   auto-promote to queen. Capturing the king ends the game.
═══════════════════════════════════════════════════════════ */
type Sq = string | null;

const START: Sq[][] = [
  ["bR", "bN", "bB", "bQ", "bK", "bB", "bN", "bR"],
  ["bP", "bP", "bP", "bP", "bP", "bP", "bP", "bP"],
  [null, null, null, null, null, null, null, null],
  [null, null, null, null, null, null, null, null],
  [null, null, null, null, null, null, null, null],
  [null, null, null, null, null, null, null, null],
  ["wP", "wP", "wP", "wP", "wP", "wP", "wP", "wP"],
  ["wR", "wN", "wB", "wQ", "wK", "wB", "wN", "wR"],
];

const GLYPH: Record<string, string> = {
  wK: "♔", wQ: "♕", wR: "♖", wB: "♗", wN: "♘", wP: "♙",
  bK: "♚", bQ: "♛", bR: "♜", bB: "♝", bN: "♞", bP: "♟",
};

const inBoard = (r: number, c: number) => r >= 0 && r < 8 && c >= 0 && c < 8;

const legalMoves = (b: Sq[][], r: number, c: number): [number, number][] => {
  const p = b[r][c];
  if (!p) return [];
  const side = p[0], kind = p[1];
  const out: [number, number][] = [];
  const enemy = (rr: number, cc: number) => !!b[rr][cc] && b[rr][cc]![0] !== side;
  const free = (rr: number, cc: number) => !b[rr][cc];

  const ray = (dr: number, dc: number) => {
    let rr = r + dr, cc = c + dc;
    while (inBoard(rr, cc)) {
      if (free(rr, cc)) out.push([rr, cc]);
      else { if (enemy(rr, cc)) out.push([rr, cc]); break; }
      rr += dr; cc += dc;
    }
  };

  if (kind === "P") {
    const dir = side === "w" ? -1 : 1;
    const start = side === "w" ? 6 : 1;
    if (inBoard(r + dir, c) && free(r + dir, c)) {
      out.push([r + dir, c]);
      if (r === start && free(r + 2 * dir, c)) out.push([r + 2 * dir, c]);
    }
    for (const dc of [-1, 1]) {
      const rr = r + dir, cc = c + dc;
      if (inBoard(rr, cc) && enemy(rr, cc)) out.push([rr, cc]);
    }
  } else if (kind === "N") {
    for (const [dr, dc] of [[-2, -1], [-2, 1], [-1, -2], [-1, 2], [1, -2], [1, 2], [2, -1], [2, 1]]) {
      const rr = r + dr, cc = c + dc;
      if (inBoard(rr, cc) && (free(rr, cc) || enemy(rr, cc))) out.push([rr, cc]);
    }
  } else if (kind === "B") {
    ([[-1, -1], [-1, 1], [1, -1], [1, 1]] as [number, number][]).forEach(([dr, dc]) => ray(dr, dc));
  } else if (kind === "R") {
    ([[-1, 0], [1, 0], [0, -1], [0, 1]] as [number, number][]).forEach(([dr, dc]) => ray(dr, dc));
  } else if (kind === "Q") {
    ([[-1, -1], [-1, 1], [1, -1], [1, 1], [-1, 0], [1, 0], [0, -1], [0, 1]] as [number, number][]).forEach(([dr, dc]) => ray(dr, dc));
  } else if (kind === "K") {
    for (let dr = -1; dr <= 1; dr++) for (let dc = -1; dc <= 1; dc++) {
      if (!dr && !dc) continue;
      const rr = r + dr, cc = c + dc;
      if (inBoard(rr, cc) && (free(rr, cc) || enemy(rr, cc))) out.push([rr, cc]);
    }
  }
  return out;
};

export const ChessGame: React.FC<{ theme?: Partial<GamesTheme> }> = ({ theme }) => {
  const HEART = useGamesTheme(theme);
  const [board, setBoard] = useState<Sq[][]>(() => START.map(r => [...r]));
  const [sel, setSel] = useState<[number, number] | null>(null);
  const [turn, setTurn] = useState<"w" | "b">("w");
  const [winner, setWinner] = useState<"w" | "b" | null>(null);
  const [taken, setTaken] = useState<string[]>([]);

  const moves = useMemo(() => (sel ? legalMoves(board, sel[0], sel[1]) : []), [board, sel]);

  const tap = (r: number, c: number) => {
    if (winner) return;
    const piece = board[r][c];

    if (sel && moves.some(([mr, mc]) => mr === r && mc === c)) {
      const b = board.map(row => [...row]);
      const captured = b[r][c];
      let moving = b[sel[0]][sel[1]]!;
      if (moving[1] === "P" && (r === 0 || r === 7)) moving = `${moving[0]}Q`;
      b[r][c] = moving;
      b[sel[0]][sel[1]] = null;
      setBoard(b);
      setSel(null);
      if (captured) {
        setTaken(t => [...t, captured]);
        if (captured[1] === "K") { setWinner(turn); return; }
      }
      setTurn(t => (t === "w" ? "b" : "w"));
      return;
    }

    if (piece && piece[0] === turn) setSel([r, c]);
    else setSel(null);
  };

  const restart = () => {
    setBoard(START.map(r => [...r]));
    setSel(null); setTurn("w"); setWinner(null); setTaken([]);
  };

  const cell = 44;
  const light = "#F3E3C4";
  const dark = "#9E7A4F";

  return (
    <div className="gk-scope" style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 14 }}>
      <GamesStyles theme={theme} />
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: cell * 8 }}>
        <div>
          <div className="gk-chip">Turn</div>
          <div className="gk-score">{winner ? "—" : turn === "w" ? "White" : "Black"}</div>
        </div>
        <button className="gk-iconbtn" onClick={restart} title="Reset board"><RotateCcw size={15} color={HEART.dark} /></button>
      </div>

      <div style={{
        position: "relative", width: cell * 8, height: cell * 8,
        border: `3px solid ${HEART.dark}`, borderRadius: 14, overflow: "hidden",
        boxShadow: "0 14px 30px rgba(27,14,6,0.18)",
      }}>
        {board.map((row, r) => row.map((p, c) => {
          const isSel = !!sel && sel[0] === r && sel[1] === c;
          const isTarget = moves.some(([mr, mc]) => mr === r && mc === c);
          return (
            <div key={`${r}-${c}`} onClick={() => tap(r, c)} style={{
              position: "absolute", left: c * cell, top: r * cell, width: cell, height: cell,
              background: isSel ? HEART.gold : (r + c) % 2 === 0 ? light : dark,
              display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", userSelect: "none",
            }}>
              {p && (
                <span style={{
                  fontSize: 30, lineHeight: 1,
                  color: p[0] === "w" ? "#FFFFFF" : "#1B0E06",
                  textShadow: p[0] === "w"
                    ? "0 1px 0 rgba(0,0,0,0.45), 0 2px 3px rgba(0,0,0,0.35)"
                    : "0 1px 0 rgba(255,255,255,0.35)",
                }}>{GLYPH[p]}</span>
              )}
              {isTarget && (
                <div style={{
                  position: "absolute",
                  width: p ? cell - 6 : 13, height: p ? cell - 6 : 13,
                  borderRadius: "50%",
                  background: p ? "transparent" : "rgba(200,32,46,0.55)",
                  border: p ? `3px solid ${HEART.red}` : "none",
                  pointerEvents: "none",
                }} />
              )}
            </div>
          );
        }))}

        <AnimatePresence>
          {winner && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              style={{ position: "absolute", inset: 0, background: "rgba(27,14,6,0.80)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 14 }}>
              <span style={{ fontFamily: "'Sora',sans-serif", fontSize: 22, fontWeight: 900, color: "#fff" }}>
                {winner === "w" ? "White" : "Black"} wins
              </span>
              <button className="gk-restart" onClick={restart}><RotateCcw size={13} /> New game</button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {taken.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 3, maxWidth: cell * 8, justifyContent: "center" }}>
          {taken.map((t, i) => (
            <span key={i} style={{ fontSize: 17, opacity: 0.55, color: t[0] === "w" ? HEART.textMuted : HEART.dark }}>{GLYPH[t]}</span>
          ))}
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   GAME TILE (launcher button) + glyphs
═══════════════════════════════════════════════════════════ */
const ChessGlyph: React.FC = () => (
  <svg width="46" height="46" viewBox="0 0 46 46" fill="none">
    <rect x="6" y="6" width="34" height="34" rx="4" fill="rgba(255,255,255,0.14)" />
    {[0, 1, 2, 3].map(r => [0, 1, 2, 3].map(c => (
      (r + c) % 2 === 0 ? <rect key={`${r}-${c}`} x={6 + c * 8.5} y={6 + r * 8.5} width="8.5" height="8.5" fill="rgba(255,255,255,0.55)" /> : null
    )))}
    <rect x="6" y="6" width="34" height="34" rx="4" stroke="rgba(255,255,255,0.9)" strokeWidth="2" />
  </svg>
);
const BlockGlyph: React.FC = () => (
  <svg width="46" height="46" viewBox="0 0 46 46" fill="none">
    <rect x="7" y="20" width="14" height="14" rx="3" fill="rgba(255,255,255,0.85)" />
    <rect x="23" y="20" width="14" height="14" rx="3" fill="rgba(255,255,255,0.55)" />
    <rect x="15" y="6" width="14" height="14" rx="3" fill="rgba(255,255,255,0.7)" />
  </svg>
);
const SnakeGlyph: React.FC = () => (
  <svg width="46" height="46" viewBox="0 0 46 46" fill="none">
    <path d="M8 34 L8 20 L22 20 L22 10 L38 10" stroke="rgba(255,255,255,0.9)" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" />
    <circle cx="38" cy="34" r="4" fill="rgba(255,255,255,0.75)" />
  </svg>
);
const GAME_GLYPHS: Record<GameId, React.FC> = { chess: ChessGlyph, block: BlockGlyph, snake: SnakeGlyph };

export const GameTile: React.FC<{ game: GameDef; delay?: number; onPlay: () => void; theme?: Partial<GamesTheme> }> = ({ game, delay = 0, onPlay, theme }) => {
  const t = useGamesTheme(theme);
  const Glyph = GAME_GLYPHS[game.id];
  return (
    <div className="gk-scope">
      <GamesStyles theme={theme} />
      <motion.button
        className="gk-game"
        initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
        transition={{ duration: 0.4, delay }} onClick={onPlay} title={`Play ${game.label}`}
      >
        <div style={{ position: "absolute", left: 6, top: 10, width: "100%", height: "100%", borderRadius: 24, background: game.side, border: `3px solid ${t.dark}`, zIndex: 0 }} />
        <div className="gk-game-face" style={{ background: game.face, zIndex: 1 }}>
          <Glyph />
          <span className="gk-game-label">{game.label}</span>
        </div>
      </motion.button>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   GAME SHELL — desktop floating 3D card / mobile bottom sheet
═══════════════════════════════════════════════════════════ */
export const GameShell: React.FC<{
  game: GameId | null;
  onClose: () => void;
  isMobile?: boolean;
  theme?: Partial<GamesTheme>;
}> = ({ game, onClose, isMobile = false, theme }) => {
  const HEART = useGamesTheme(theme);

  useEffect(() => {
    if (!game) return;
    const onEsc = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onEsc);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onEsc); document.body.style.overflow = ""; };
  }, [game, onClose]);

  const body =
    game === "chess" ? <ChessGame theme={theme} /> :
    game === "block" ? <BlocksGame theme={theme} /> :
    game === "snake" ? <SnakeGame theme={theme} /> : null;

  const Head: React.FC<{ id: GameId }> = ({ id }) => (
    <>
      <div className="gk-shell-head">
        <span className="gk-shell-title">{GAME_TITLES[id]}</span>
        <button className="gk-iconbtn" onClick={onClose} aria-label="Close"><X size={16} color={HEART.dark} /></button>
      </div>
      <p className="gk-hint">{GAME_HINTS[id]}</p>
    </>
  );

  return (
    <div className="gk-scope">
      <GamesStyles theme={theme} />
      <AnimatePresence>
        {game && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={onClose}
              style={{ position: "fixed", inset: 0, background: "rgba(27,14,6,0.58)", backdropFilter: "blur(5px)", zIndex: 6000 }}
            />

            {isMobile ? (
              <motion.div
                key="sheet"
                initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
                transition={{ type: "tween", duration: 0.24 }}
                style={{
                  position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 6001,
                  background: HEART.panelBg, borderTopLeftRadius: 24, borderTopRightRadius: 24,
                  maxHeight: "92vh", overflowY: "auto",
                  boxShadow: "0 -12px 34px rgba(27,14,6,0.22)",
                  paddingBottom: "calc(20px + env(safe-area-inset-bottom))",
                }}
              >
                <div style={{ display: "flex", justifyContent: "center", padding: "10px 0 2px" }}>
                  <div style={{ width: 40, height: 4, borderRadius: 99, background: HEART.border }} />
                </div>
                <Head id={game} />
                <div style={{ padding: "18px 16px" }}>{body}</div>
              </motion.div>
            ) : (
              <div style={{
                position: "fixed", inset: 0, zIndex: 6001,
                display: "flex", alignItems: "center", justifyContent: "center",
                padding: 32, pointerEvents: "none",
              }}>
                <motion.div
                  key="modal"
                  initial={{ opacity: 0, scale: 0.9, y: 26 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.9, y: 26 }}
                  transition={{ type: "spring", stiffness: 300, damping: 26 }}
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    position: "relative", pointerEvents: "auto",
                    width: "min(560px, calc(100vw - 64px))",
                    maxHeight: "calc(100vh - 64px)", overflowY: "auto",
                    background: HEART.panelBg,
                    borderRadius: 28,
                    border: `4px solid ${HEART.dark}`,
                    boxShadow: `14px 18px 0 ${HEART.redDark}, 0 40px 90px rgba(27,14,6,0.45)`,
                  }}
                >
                  <div style={{
                    height: 6,
                    background: `linear-gradient(90deg, ${HEART.red}, ${HEART.gold}, ${HEART.goldDark}, ${HEART.red})`,
                    backgroundSize: "300% 100%", animation: "gkShimmer 4s linear infinite",
                  }} />
                  <Head id={game} />
                  <div style={{ padding: "20px 28px 32px" }}>{body}</div>
                </motion.div>
              </div>
            )}
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   CONVENIENCE HOOK + STATE
═══════════════════════════════════════════════════════════ */

/** Tracks which game is open and whether the viewport is mobile-width.
 *  Use this if you want the tiles + shell wired up but laid out yourself. */
export const useGamesState = (mobileBreakpoint = 768) => {
  const [activeGame, setActiveGame] = useState<GameId | null>(null);
  const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" && window.innerWidth <= mobileBreakpoint);

  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth <= mobileBreakpoint);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, [mobileBreakpoint]);

  return {
    activeGame,
    isMobile,
    open: (id: GameId) => setActiveGame(id),
    close: () => setActiveGame(null),
  };
};

/* ═══════════════════════════════════════════════════════════
   GamesPanel — full "pick a game" section, ready to drop in.
═══════════════════════════════════════════════════════════ */
export interface GamesPanelProps {
  games?: GameDef[];
  title?: string | null;
  subtitle?: string | null;
  theme?: Partial<GamesTheme>;
  gap?: number;
}

const GamesPanel: React.FC<GamesPanelProps> = ({
  games = GAMES,
  title = "Let's play a game",
  subtitle = "Kill a few minutes while we work",
  theme,
  gap = 34,
}) => {
  const t = useGamesTheme(theme);
  const { activeGame, isMobile, open, close } = useGamesState();

  return (
    <div className="gk-scope" style={{ textAlign: "center" }}>
      <GamesStyles theme={theme} />
      {title && (
        <p style={{ fontFamily: "'Sora', sans-serif", fontWeight: 900, letterSpacing: "-0.03em", color: t.dark, fontSize: 28, marginBottom: 8 }}>
          {title}
        </p>
      )}
      {subtitle && (
        <p style={{ fontSize: 14, fontWeight: 600, color: t.textMuted, marginBottom: 40 }}>{subtitle}</p>
      )}
      <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap }}>
        {games.map((g, i) => (
          <GameTile key={g.id} game={g} delay={i * 0.09} onPlay={() => open(g.id)} theme={theme} />
        ))}
      </div>
      <GameShell game={activeGame} onClose={close} isMobile={isMobile} theme={theme} />
    </div>
  );
};

export default GamesPanel;
